import React from 'react';
import {ThemeProvider as MuiThemeProvider, Theme as MuiTheme} from '@mui/material/styles';
import {CssBaseline} from '../CssBaseline';
import {jdGreen} from '../themes';
import {css} from '@emotion/react';
import GlobalStyles from '@mui/material/GlobalStyles';
import * as jdGreenTokens from '@deere/fuel-tokens/dist/web/jd-green/tokens';
import * as jdBlackTokens from '@deere/fuel-tokens/dist/web/jd-black/tokens';
import type {ThemeProviderProps as MuiThemeProviderProps} from '@mui/material/styles/ThemeProvider';

type Tokens = {
    [Key in keyof (typeof jdGreenTokens | typeof jdBlackTokens)]: typeof jdGreenTokens[Key] | typeof jdBlackTokens[Key];
};

declare module '@mui/material/styles/createTheme' {
    interface Theme {
        name: 'jdBlack' | 'jdGreen';
        tokens: Tokens;
    }

    interface ThemeOptions {
        name: 'jdBlack' | 'jdGreen';
        tokens: Tokens;
    }
}

/*
    Exporting the overriden theme from @mui/material
*/
export type Theme = MuiTheme;

export interface ThemeProviderProps extends Omit<MuiThemeProviderProps,
'theme'
> {
    /*
      Include CSS Baseline which brings in global CSS to application.
    */
    baseline?: boolean;
    /*
      Include font files
    */
    includeFonts?: boolean;
    /**
     * @default jdGreen
     */
    theme?: Partial<MuiTheme>;
}

/**
 * Keeping a string because of JSS error 'duplicate-key' for multiple '@font-face'
 */
const fontFace = css`
    /* Open Sans - Regular v18 vietnamese_latin-ext_latin_greek-ext_greek_cyrillic-ext_cyrillic */
    @font-face {
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: 400;
        src: url('https://cdn.ux.deere.com/brand-foundations/1.3.1/typography/open-sans-v18-regular.woff2') format('woff2'),
                url('https://cdn.ux.deere.com/brand-foundations/1.3.1/typography/open-sans-v18-regular.woff') format('woff')
    }

    /* Open Sans - Semibold v18 vietnamese_latin-ext_latin_greek-ext_greek_cyrillic-ext_cyrillic */
    @font-face {
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: 600;
        src: url('https://cdn.ux.deere.com/brand-foundations/1.3.1/typography/open-sans-v18-600.woff2') format('woff2'),
                url('https://cdn.ux.deere.com/brand-foundations/1.3.1/typography/open-sans-v18-600.woff') format('woff')
    }

    /* Open Sans - Bold v18 vietnamese_latin-ext_latin_greek-ext_greek_cyrillic-ext_cyrillic */
    @font-face {
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: 700;
        src: url('https://cdn.ux.deere.com/brand-foundations/1.3.1/typography/open-sans-v18-700.woff2') format('woff2'),
                url('https://cdn.ux.deere.com/brand-foundations/1.3.1/typography/open-sans-v18-700.woff') format('woff')
    }

    /* JDSansPro - Medium */
    @font-face {
        font-family: 'JDSansPro-Medium';
        font-style: normal;
        font-weight: normal;
        src: url('https://cdn.ux.deere.com/brand-foundations/1.3.1/typography/JDSansPro-Medium.woff2') format('woff2'),
            url('https://cdn.ux.deere.com/brand-foundations/1.3.1/typography/JDSansPro-Medium.woff') format('woff')
    }
`;

const fontFaceStyles = <GlobalStyles styles={fontFace}/>;

export const ThemeProvider = (
    {
        children,
        baseline = true,
        includeFonts = true,
        theme = jdGreen,
        ...props
    }: ThemeProviderProps): React.ReactElement => {
    return (
        <MuiThemeProvider
            theme={theme}
            {...props}
        >
            <div data-theme={theme.name === 'jdBlack' ? 'fuel-jd-black' : 'fuel-jd-green'}>
                {baseline && <CssBaseline/>}
                {includeFonts ? fontFaceStyles : undefined}
                {children}
            </div>
        </MuiThemeProvider>
    );
};
